import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  addAdminApi,
  updateAdminApi,
  roleActiveListApi,
  uploadFile,
  adminDetailsApi
} from "store/actions";
import { useDispatch } from "react-redux";

const DefaultProfileIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="12" fill="#E9ECEF" />
    <circle cx="12" cy="9.5" r="3.5" fill="#ADB5BD" />
    <path
      d="M4.5 19.5C5.8 16.4 8.6 15 12 15C15.4 15 18.2 16.4 19.5 19.5"
      stroke="#ADB5BD"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const getValidationSchema = (isEdit, isSubAdmin) =>
  Yup.object().shape({
    role: isSubAdmin
      ? Yup.string().notRequired()
      : Yup.string()
        .oneOf(["ADMIN", "SUPERADMIN"], "Invalid role")
        .required("Role is required"),
    roleId: Yup.string().when("role", {
      is: "ADMIN",
      then: (schema) => schema.required("Role Id is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: isEdit
      ? Yup.string().notRequired()
      : Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    countryCode: Yup.string().notRequired(),
    mobileNumber: Yup.string().notRequired(),
    profilePic: Yup.mixed().notRequired(),
    isEditChanges: Yup.boolean().when("role", {
      is: "ADMIN",
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.notRequired(),
    })
  });

const emptyValues = {
  role: "",
  roleId: "",
  name: "",
  email: "",
  password: "",
  countryCode: "+91",
  mobileNumber: "",
  profilePic: "",
  isEditChanges: false,
};

export default function AddUpdateAdmin({
  show,
  handleClose,
  handleConfirm,
  rowData,
}) {
  const dispatch = useDispatch();
  const isEdit = Boolean(rowData?._id);

  const [adminDetails, setAdminDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const isSubAdmin = adminDetails?.role === "SUBADMIN";

  const [initialValues, setInitialValues] = useState(emptyValues);
  const [profilePreview, setProfilePreview] = useState(null);

  // Role dropdown data
  const [roleList, setRoleList] = useState([]);
  const [roleListLoading, setRoleListLoading] = useState(false);

  useEffect(() => {
    fetchRoleList();
  }, []);

  const fetchRoleList = () => {
    setRoleListLoading(true);
    dispatch(
      roleActiveListApi({
        data: { page: 1, limit: 1000 },
        callBack: (err, res) => {
          setRoleListLoading(false);
          if (err) return;
          setRoleList(res?.data || []);
        },
      }),
    );
  };

  useEffect(() => {
    if (!show) {
      setAdminDetails(null);
      setInitialValues(emptyValues);
      setProfilePreview(null);
      return;
    }

    if (rowData?._id) {
      fetchAdminDetails(rowData._id);
    }
  }, [show, rowData?._id]);

  const fetchAdminDetails = (id) => {
    setDetailsLoading(true);
    dispatch(
      adminDetailsApi({
        data: { _id: id },
        callBack: (err, res) => {
          setDetailsLoading(false);
          if (err) return;

          const details = res?.data || res || {};
          setAdminDetails(details);

          setInitialValues({
            role: details?.role || "",
            roleId: details?.roleDetails?._id || "",
            name: details?.name || "",
            email: details?.email || "",
            password: "",
            countryCode: details?.countryCode || "+91",
            mobileNumber: details?.mobileNumber || "",
            profilePic: details?.profilePic || "",
            isEditChanges: details?.isEditChanges ?? false
          });

          if (details?.profilePic) {
            setProfilePreview(details.profilePic);
          }
        },
      }),
    );
  };

  const validationSchema = useMemo(
    () => getValidationSchema(isEdit, isSubAdmin),
    [isEdit, isSubAdmin]
  );

  const handleProfilePicChange = async (e, setFieldValue) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => setProfilePreview(reader.result);
    reader.readAsDataURL(file);

    try {
      const response = await dispatch(uploadFile({ data: file }));
      const uploadedUrl = response?.payload?.url;
      if (uploadedUrl) {
        setFieldValue("profilePic", uploadedUrl);
      }
    } catch (error) {
      console.error("Profile pic upload failed:", error);
    }
  };

  const onSubmit = (values) => {
    const payload = { ...values };

    if (typeof payload.profilePic !== "string" || !payload.profilePic) {
      delete payload.profilePic;
    }

    if (isEdit) {
      const updatePayload = { ...payload, _id: rowData?._id || rowData?.id };
      delete updatePayload.email;
      delete updatePayload.password;
      dispatch(
        updateAdminApi({
          data: updatePayload,
          callBack: (err, res) => {
            if (err) return;
            if (res) {
              handleConfirm();
              handleClose();
            }
          },
        }),
      );
    } else {
      dispatch(
        addAdminApi({
          data: payload,
          callBack: (err, res) => {
            if (err) return;
            if (res) {
              handleConfirm();
              handleClose();
            }
          },
        }),
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header className="py-3 text-dark" closeButton>
        <Modal.Title className="fw-bold text-dark">
          {isEdit ? "Edit Admin" : "Add Admin"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {detailsLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              onSubmit(values);
            }}
          >
            {({ values, setFieldValue }) => {
              return (
                <Form>
                  {/* 1. Role (hidden if editing an existing Subadmin) */}
                  {!isSubAdmin && (
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="role" className="form-label">
                          Role
                        </label>
                        <Field
                          as="select"
                          name="role"
                          className="form-control"
                          onChange={(e) => {
                            setFieldValue("role", e.target.value);
                            if (e.target.value !== "ADMIN") {
                              setFieldValue("roleId", "");
                            }
                          }}
                        >
                          <option value="">Select Role</option>
                          <option value="ADMIN">Admin</option>
                          <option value="SUPERADMIN">Super Admin</option>
                        </Field>
                        <ErrorMessage
                          name="role"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </div>

                      {values.role === "ADMIN" && (
                        <div className="col-md-6 mb-3">
                          <label htmlFor="roleId" className="form-label">
                            Role
                          </label>
                          <Field
                            as="select"
                            name="roleId"
                            className="form-control"
                            disabled={roleListLoading}
                          >
                            <option value="">
                              {roleListLoading ? "Loading..." : "Select Role"}
                            </option>
                            {roleList.map((role) => (
                              <option key={role._id} value={role._id}>
                                {role.roleName}
                              </option>
                            ))}
                            {adminDetails?.roleDetails?._id &&
                              !roleList.some(
                                (role) => role._id === adminDetails.roleDetails._id
                              ) && (
                                <option value={adminDetails.roleDetails._id}>
                                  {adminDetails.roleDetails.roleName}
                                </option>
                              )}
                          </Field>
                          {!roleListLoading && roleList.length === 0 && (
                            <small className="text-muted d-block mt-1">
                              No roles found
                            </small>
                          )}
                          <ErrorMessage
                            name="roleId"
                            component="div"
                            className="text-danger mt-1"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* 3. Name / 4. Email */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="name" className="form-label">
                        Name
                      </label>
                      <Field name="name" type="text" className="form-control" />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="email" className="form-label">
                        Email
                      </label>
                      <Field
                        name="email"
                        type="email"
                        className="form-control"
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </div>
                  </div>

                  {/* 5. Password - only on Add */}
                  {!isEdit && (
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="password" className="form-label">
                          Password
                        </label>
                        <Field
                          name="password"
                          type="password"
                          className="form-control"
                        />
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </div>
                    </div>
                  )}

                  {/* 6. Mobile Number with Country Code - optional */}
                  <div className="row">
                    <div className="col-md-2 mb-3">
                      <label htmlFor="countryCode" className="form-label">
                        Code
                      </label>
                      <Field
                        name="countryCode"
                        type="text"
                        placeholder="+91"
                        className="form-control"
                      />
                      <ErrorMessage
                        name="countryCode"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label htmlFor="mobileNumber" className="form-label">
                        Mobile Number
                      </label>
                      <Field
                        name="mobileNumber"
                        type="text"
                        placeholder="Optional"
                        className="form-control"
                      />
                      <ErrorMessage
                        name="mobileNumber"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </div>

                    {/* 7. Profile Pic - optional, default icon */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="profilePic" className="form-label">
                        Profile Pic
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        {profilePreview ? (
                          <img
                            src={profilePreview}
                            alt="Profile preview"
                            width={64}
                            height={64}
                            style={{ borderRadius: "50%", objectFit: "cover" }}
                          />
                        ) : (
                          <DefaultProfileIcon />
                        )}
                        <input
                          id="profilePic"
                          name="profilePic"
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={(e) =>
                            handleProfilePicChange(e, setFieldValue)
                          }
                        />
                      </div>
                      <ErrorMessage
                        name="profilePic"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </div>
                  </div>

                  {/* 8. Edit Changes - only when Role = ADMIN, boolean, default No */}
                  {values.role === "ADMIN" && (
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="isEditChanges" className="form-label d-block">
                          Edit Changes
                        </label>
                        <div className="form-check form-check-inline">
                          <Field
                            type="radio"
                            name="isEditChanges"
                            value="true"
                            checked={values.isEditChanges === true}
                            onChange={() => setFieldValue("isEditChanges", true)}
                            className="form-check-input"
                            id="editChangesYes"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="editChangesYes"
                          >
                            Yes
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <Field
                            type="radio"
                            name="isEditChanges"
                            value="false"
                            checked={values.isEditChanges === false}
                            onChange={() => setFieldValue("isEditChanges", false)}
                            className="form-check-input"
                            id="editChangesNo"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="editChangesNo"
                          >
                            No
                          </label>
                        </div>
                        <ErrorMessage
                          name="isEditChanges"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </div>
                    </div>
                  )}

                  <div className="d-flex justify-content-end">
                    <button type="submit" className="btn btn-primary">
                      Submit
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </Modal.Body>
    </Modal>
  );
}